import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { cn, formatCurrency } from '../../lib/utils';
import { Search, XCircle, TrendingUp, DollarSign, Clock, RefreshCw, MoreHorizontal, Sparkles, CheckCircle2, X, FileText, Scale, Zap } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const INITIAL_DENIALS = [
  { id: 'DEN-001', claimId: 'CLM-001', patient: 'John Smith', patientId: '1', payer: 'Blue Cross', amount: 450.00, reason: 'CO-4', reasonDesc: 'Procedure code inconsistent with modifier', dos: '2026-01-15', status: 'open', daysOpen: 15, aiSuggestion: 'Add modifier 25 to E/M code', aiDetail: 'The E/M service (99213) was billed on the same date as a procedure. Modifier 25 should be appended to indicate a separately identifiable E/M service was performed. This is a common bundling issue with Blue Cross.', audit: [{ action: 'Claim Denied', date: '2026-01-20', user: 'Blue Cross', detail: 'CO-4: Procedure code inconsistent with modifier' }] },
  { id: 'DEN-002', claimId: 'CLM-008', patient: 'Maria Garcia', patientId: '2', payer: 'Aetna', amount: 1250.00, reason: 'CO-16', reasonDesc: 'Missing information', dos: '2026-01-10', status: 'appealed', daysOpen: 20, aiSuggestion: 'Submit medical records with appeal', aiDetail: 'Aetna requires supporting documentation for claims over $1,000. Submit the office visit notes and any relevant diagnostic reports with the appeal. Historical success rate for similar appeals: 78%.', audit: [{ action: 'Claim Denied', date: '2026-01-15', user: 'Aetna', detail: 'CO-16: Missing information' }, { action: 'Appeal Filed', date: '2026-01-18', user: 'Biller', detail: 'Medical records attached' }] },
  { id: 'DEN-003', claimId: 'CLM-015', patient: 'Robert Johnson', patientId: '3', payer: 'UnitedHealth', amount: 875.00, reason: 'PR-1', reasonDesc: 'Deductible', dos: '2026-01-08', status: 'patient_resp', daysOpen: 22, aiSuggestion: 'Bill patient for deductible amount', aiDetail: 'Patient has not met their annual deductible. The full $875.00 is the patient\'s responsibility. Recommend generating a patient statement and offering a payment plan option.', audit: [{ action: 'Claim Denied', date: '2026-01-12', user: 'UnitedHealth', detail: 'PR-1: Deductible not met' }] },
  { id: 'DEN-004', claimId: 'CLM-022', patient: 'Emily Davis', patientId: '4', payer: 'Medicare', amount: 325.00, reason: 'CO-97', reasonDesc: 'Benefit for this service is included in another service', dos: '2026-01-05', status: 'open', daysOpen: 25, aiSuggestion: 'Review bundling rules - may need appeal', aiDetail: 'Medicare bundled CPT 99213 with CPT 36415 (venipuncture). Consider appealing with documentation that the E/M was a separately identifiable service, or review if modifier 25 applies.', audit: [{ action: 'Claim Denied', date: '2026-01-08', user: 'Medicare', detail: 'CO-97: Bundling' }] },
  { id: 'DEN-005', claimId: 'CLM-029', patient: 'Lisa Anderson', patientId: '5', payer: 'Cigna', amount: 2100.00, reason: 'CO-50', reasonDesc: 'Non-covered service', dos: '2026-01-02', status: 'written_off', daysOpen: 28, aiSuggestion: 'Service not in patient benefit plan', aiDetail: 'The procedure (CPT 27447 - Total Knee Arthroplasty) is not covered under the patient\'s current Cigna plan. Recommend verifying benefits before future procedures and billing the patient directly.', audit: [{ action: 'Claim Denied', date: '2026-01-05', user: 'Cigna', detail: 'CO-50: Non-covered' }, { action: 'Written Off', date: '2026-01-20', user: 'Biller', detail: 'Service not covered, patient notified' }] },
];

const DENIAL_REASONS = [
  { reason: 'Missing Info (CO-16)', count: 28, amount: 18500 },
  { reason: 'Not Covered (CO-50)', count: 22, amount: 15200 },
  { reason: 'Bundling (CO-97)', count: 18, amount: 12400 },
  { reason: 'Modifier Issue (CO-4)', count: 15, amount: 9800 },
  { reason: 'Auth Required (CO-15)', count: 12, amount: 8500 },
];

const DENIAL_BY_PAYER = [
  { name: 'Blue Cross', value: 32 },
  { name: 'Aetna', value: 24 },
  { name: 'UnitedHealth', value: 18 },
  { name: 'Medicare', value: 14 },
  { name: 'Cigna', value: 12 },
];

const COLORS = ['#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db'];

const HEATMAP_DATA = [
  { payer: 'Blue Cross', cpt: '99213', denials: 8, rate: 12 },
  { payer: 'Blue Cross', cpt: '99214', denials: 5, rate: 8 },
  { payer: 'Blue Cross', cpt: '93000', denials: 3, rate: 15 },
  { payer: 'Aetna', cpt: '99213', denials: 6, rate: 10 },
  { payer: 'Aetna', cpt: '99214', denials: 9, rate: 18 },
  { payer: 'Aetna', cpt: '36415', denials: 2, rate: 5 },
  { payer: 'UnitedHealth', cpt: '99213', denials: 4, rate: 7 },
  { payer: 'UnitedHealth', cpt: '99214', denials: 7, rate: 14 },
  { payer: 'UnitedHealth', cpt: '99215', denials: 3, rate: 22 },
  { payer: 'Medicare', cpt: '99213', denials: 2, rate: 4 },
  { payer: 'Medicare', cpt: '99214', denials: 4, rate: 9 },
  { payer: 'Medicare', cpt: '36415', denials: 6, rate: 25 },
  { payer: 'Cigna', cpt: '99213', denials: 3, rate: 8 },
  { payer: 'Cigna', cpt: '99214', denials: 5, rate: 11 },
  { payer: 'Cigna', cpt: '93000', denials: 4, rate: 20 },
];

const DenialHeatmap = () => {
  const payers = [...new Set(HEATMAP_DATA.map(d => d.payer))];
  const cpts = [...new Set(HEATMAP_DATA.map(d => d.cpt))];

  const getCellColor = (rate) => {
    if (rate >= 20) return 'bg-red-500 text-white';
    if (rate >= 15) return 'bg-red-300 text-red-900';
    if (rate >= 10) return 'bg-amber-300 text-amber-900';
    if (rate >= 5) return 'bg-amber-100 text-amber-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  const getCell = (payer, cpt) => HEATMAP_DATA.find(d => d.payer === payer && d.cpt === cpt);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="text-left p-2 text-muted-foreground font-medium">Payer / CPT</th>
            {cpts.map(cpt => <th key={cpt} className="p-2 text-center font-mono font-medium">{cpt}</th>)}
          </tr>
        </thead>
        <tbody>
          {payers.map(payer => (
            <tr key={payer}>
              <td className="p-2 font-medium text-slate-700">{payer}</td>
              {cpts.map(cpt => {
                const cell = getCell(payer, cpt);
                return (
                  <td key={cpt} className="p-1 text-center">
                    {cell ? (
                      <div className={cn("rounded-lg px-2 py-1.5 font-medium", getCellColor(cell.rate))} data-testid={`heatmap-${payer.replace(/\s/g,'')}-${cpt}`}>
                        {cell.rate}%
                        <p className="text-[9px] font-normal opacity-70">{cell.denials} denied</p>
                      </div>
                    ) : (
                      <div className="rounded-lg px-2 py-1.5 bg-slate-50 text-slate-300">—</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-3 mt-3 pt-2 border-t">
        <span className="text-[10px] text-muted-foreground">Denial Rate:</span>
        {[
          { label: '< 5%', color: 'bg-emerald-100' },
          { label: '5-10%', color: 'bg-amber-100' },
          { label: '10-15%', color: 'bg-amber-300' },
          { label: '15-20%', color: 'bg-red-300' },
          { label: '> 20%', color: 'bg-red-500' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className={cn("h-3 w-6 rounded", l.color)} />
            <span className="text-[10px] text-muted-foreground">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RevQDenials = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [denials, setDenials] = useState(INITIAL_DENIALS);
  const [selectedDenial, setSelectedDenial] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [appealReason, setAppealReason] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const openModal = (denial, type) => {
    setSelectedDenial(denial);
    setModalType(type);
    setAppealReason('');
  };

  const closeModal = () => {
    setSelectedDenial(null);
    setModalType(null);
    setAppealReason('');
  };

  const handleAcceptAiFix = () => {
    const auditEntry = { action: 'AI Fix Applied & Resubmitted', date: new Date().toISOString().split('T')[0], user: 'System AI', detail: selectedDenial.aiSuggestion };
    setDenials(prev => prev.map(d =>
      d.id === selectedDenial.id ? { ...d, status: 'resolved', audit: [...(d.audit || []), auditEntry] } : d
    ));
    toast.success(`AI fix applied & resubmitted`, { description: `${selectedDenial.claimId} sent back to clearinghouse` });
    closeModal();
  };

  const handleRejectAiFix = () => {
    toast.info(`AI fix rejected for ${selectedDenial.claimId}`);
    closeModal();
  };

  const handleOneClickResubmit = (denial) => {
    const auditEntry = { action: 'One-Click Resubmit', date: new Date().toISOString().split('T')[0], user: 'Biller', detail: 'Resubmitted without changes' };
    setDenials(prev => prev.map(d =>
      d.id === denial.id ? { ...d, status: 'appealed', audit: [...(d.audit || []), auditEntry] } : d
    ));
    toast.success(`${denial.claimId} resubmitted to clearinghouse`);
  };

  const handleFileAppeal = () => {
    if (!appealReason.trim()) {
      toast.error('Please provide a reason for the appeal');
      return;
    }
    setDenials(prev => prev.map(d =>
      d.id === selectedDenial.id ? { ...d, status: 'appealed' } : d
    ));
    toast.success(`Appeal filed for ${selectedDenial.claimId}`, { description: 'Appeal has been submitted to the payer' });
    closeModal();
  };

  const handleMarkResolved = (denial) => {
    setDenials(prev => prev.map(d =>
      d.id === denial.id ? { ...d, status: 'resolved' } : d
    ));
    toast.success(`${denial.claimId} marked as resolved`);
  };

  const handleWriteOff = (denial) => {
    setDenials(prev => prev.map(d =>
      d.id === denial.id ? { ...d, status: 'written_off' } : d
    ));
    toast.success(`${denial.claimId} written off`, { description: formatCurrency(denial.amount) + ' removed from A/R' });
  };

  const handleQuickAiFix = (denial) => {
    const auditEntry = { action: 'AI Fix Applied & Resubmitted', date: new Date().toISOString().split('T')[0], user: 'System AI', detail: denial.aiSuggestion };
    setDenials(prev => prev.map(d =>
      d.id === denial.id ? { ...d, status: 'resolved', audit: [...(d.audit || []), auditEntry] } : d
    ));
    toast.success(`AI fix applied & claim resent`, { description: `${denial.claimId} — ${denial.aiSuggestion}` });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectableFiltered = (list) => list.filter(d => d.status === 'open' || d.status === 'in_review');

  const toggleSelectAll = () => {
    const selectable = selectableFiltered(filteredDenials);
    if (selectable.every(d => selectedIds.has(d.id))) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectable.forEach(d => next.delete(d.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectable.forEach(d => next.add(d.id));
        return next;
      });
    }
  };

  const handleBatchFixSelected = () => {
    const selected = denials.filter(d => selectedIds.has(d.id) && (d.status === 'open' || d.status === 'in_review'));
    if (selected.length === 0) { toast.info('No fixable denials selected'); return; }
    const recoveredAmount = selected.reduce((sum, d) => sum + d.amount, 0);
    const auditEntry = { action: 'Batch AI Fix Applied & Resubmitted', date: new Date().toISOString().split('T')[0], user: 'System AI', detail: 'Batch operation' };
    setDenials(prev => prev.map(d =>
      selectedIds.has(d.id) && (d.status === 'open' || d.status === 'in_review')
        ? { ...d, status: 'resolved', audit: [...(d.audit || []), auditEntry] }
        : d
    ));
    toast.success(`${selected.length} AI fixes applied & claims resent`, { description: `${formatCurrency(recoveredAmount)} recovered` });
    setSelectedIds(new Set());
  };

  const getStatusBadge = (status) => {
    const configs = {
      open: { label: 'Open', class: 'bg-red-50 text-red-700 border-red-200' },
      appealed: { label: 'Appealed', class: 'bg-amber-50 text-amber-700 border-amber-200' },
      patient_resp: { label: 'Patient Resp.', class: 'bg-blue-50 text-blue-700 border-blue-200' },
      resolved: { label: 'Resolved', class: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      written_off: { label: 'Written Off', class: 'bg-slate-100 text-slate-600 border-slate-200' },
    };
    const config = configs[status] || configs.open;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const filteredDenials = denials.filter(d => {
    const matchesSearch = d.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         d.claimId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleBatchAcceptAiFixes = () => {
    const fixable = denials.filter(d => d.status === 'open' && d.aiSuggestion);
    if (fixable.length === 0) { toast.info('No open denials with AI fixes to apply'); return; }
    const recoveredAmount = fixable.reduce((sum, d) => sum + d.amount, 0);
    setDenials(prev => prev.map(d =>
      d.status === 'open' && d.aiSuggestion ? { ...d, status: 'resolved' } : d
    ));
    toast.success(`${fixable.length} AI fixes applied`, { description: `${formatCurrency(recoveredAmount)} recovered` });
  };

  const totalDenied = denials.reduce((sum, d) => sum + d.amount, 0);
  const openDenials = denials.filter(d => d.status === 'open');
  const fixableDenials = denials.filter(d => d.status === 'open' && d.aiSuggestion);

  return (
    <div className="space-y-6" data-testid="denials-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Denials Management</h1>
          <p className="text-slate-500 mt-1">Track, analyze, and resolve claim denials</p>
        </div>
        <Button variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" /> Sync Denials
        </Button>
      </div>

      {/* Quick Actions Toolbar */}
      {fixableDenials.length > 0 && (
        <Card className="border-violet-200 bg-gradient-to-r from-violet-50/60 to-white" data-testid="quick-actions-toolbar">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-violet-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Quick Actions</p>
                  <p className="text-xs text-muted-foreground">{fixableDenials.length} open denial{fixableDenials.length > 1 ? 's' : ''} with AI fixes available — {formatCurrency(fixableDenials.reduce((s, d) => s + d.amount, 0))} recoverable</p>
                </div>
              </div>
              <Button onClick={handleBatchAcceptAiFixes} data-testid="batch-accept-ai-fixes-btn" className="bg-violet-600 hover:bg-violet-700 gap-2">
                <Sparkles className="h-4 w-4" /> Accept All AI Fixes ({fixableDenials.length})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center"><XCircle className="h-5 w-5 text-red-600" /></div><div><p className="text-sm text-muted-foreground">Open Denials</p><p className="text-xl font-bold text-red-600">{openDenials.length}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center"><DollarSign className="h-5 w-5 text-amber-600" /></div><div><p className="text-sm text-muted-foreground">Total Denied</p><p className="text-xl font-bold">{formatCurrency(totalDenied)}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-emerald-600" /></div><div><p className="text-sm text-muted-foreground">Recovery Rate</p><p className="text-xl font-bold text-emerald-600">68%</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><Clock className="h-5 w-5 text-blue-600" /></div><div><p className="text-sm text-muted-foreground">Avg Days Open</p><p className="text-xl font-bold">18 days</p></div></div></CardContent></Card>
      </div>

      {/* Denial Hotspot Heatmap */}
      <Card data-testid="denial-hotspot-heatmap">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            <CardTitle className="text-lg font-semibold">Denial Hotspot Map</CardTitle>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Payer + CPT code combinations with highest denial rates</p>
        </CardHeader>
        <CardContent>
          <DenialHeatmap />
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg font-semibold">Denial Reasons</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={DENIAL_REASONS} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis dataKey="reason" type="category" tick={{ fontSize: 10 }} stroke="#94a3b8" width={120} />
                  <Tooltip />
                  <Bar dataKey="count" name="Count" fill="#ef4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg font-semibold">Denials by Payer</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={DENIAL_BY_PAYER} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" nameKey="name">
                    {DENIAL_BY_PAYER.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search denials..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="denials-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="denials-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="appealed">Appealed</SelectItem>
                <SelectItem value="patient_resp">Patient Resp.</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="written_off">Written Off</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Denials Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">Denial Queue ({filteredDenials.length})</CardTitle>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-3" data-testid="batch-selection-bar">
                <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1.5 text-xs" onClick={handleBatchFixSelected} data-testid="batch-fix-selected-btn">
                  <Sparkles className="h-3 w-3" />
                  AI Fix & Resend ({selectedIds.size})
                </Button>
                <Button size="sm" variant="ghost" className="text-xs" onClick={() => setSelectedIds(new Set())} data-testid="batch-clear-selection">
                  Clear
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={selectableFiltered(filteredDenials).length > 0 && selectableFiltered(filteredDenials).every(d => selectedIds.has(d.id))}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-denials"
                  />
                </TableHead>
                <TableHead>Claim ID</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Payer</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Days Open</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Quick Action</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDenials.map((denial) => (
                <TableRow key={denial.id} className={cn("hover:bg-slate-50", selectedIds.has(denial.id) && "bg-violet-50/50")} data-testid={`denial-row-${denial.id}`}>
                  <TableCell>
                    {(denial.status === 'open' || denial.status === 'in_review') ? (
                      <Checkbox
                        checked={selectedIds.has(denial.id)}
                        onCheckedChange={() => toggleSelect(denial.id)}
                        data-testid={`select-denial-${denial.id}`}
                      />
                    ) : (
                      <div className="h-4 w-4" />
                    )}
                  </TableCell>                  <TableCell className="font-mono text-sm">{denial.claimId}</TableCell>
                  <TableCell>
                    <button onClick={() => navigate(`/revq/patients/${denial.patientId}`)} className="font-medium text-slate-800 hover:text-violet-600 hover:underline transition-colors cursor-pointer" data-testid={`denial-patient-${denial.id}`}>{denial.patient}</button>
                  </TableCell>
                  <TableCell>{denial.payer}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-mono text-sm">{denial.reason}</p>
                      <p className="text-xs text-slate-500">{denial.reasonDesc}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium text-red-600">{formatCurrency(denial.amount)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn(denial.daysOpen > 20 ? "border-red-200 text-red-600" : "")}>
                      {denial.daysOpen} days
                    </Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(denial.status)}</TableCell>
                  <TableCell>
                    {denial.status === 'open' || denial.status === 'in_review' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1.5 text-xs border-violet-200 text-violet-700 bg-violet-50/50 hover:bg-violet-100 hover:border-violet-300 transition-all"
                        onClick={() => handleQuickAiFix(denial)}
                        data-testid={`quick-aifix-${denial.id}`}
                      >
                        <Sparkles className="h-3 w-3" />
                        AI Fix & Resend
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <CheckCircle2 className="h-3 w-3" />
                        <span>Resolved</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`denial-menu-${denial.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`denial-details-${denial.id}`} onSelect={() => openModal(denial, 'details')}>
                          <FileText className="h-4 w-4 mr-2" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`denial-appeal-${denial.id}`} onSelect={() => openModal(denial, 'appeal')}>
                          <Scale className="h-4 w-4 mr-2" /> File Appeal
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`denial-aifix-${denial.id}`} onSelect={() => openModal(denial, 'ai-fix')}>
                          <Sparkles className="h-4 w-4 mr-2" /> Apply AI Fix
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`denial-resubmit-${denial.id}`} onSelect={() => handleOneClickResubmit(denial)}>
                          <RefreshCw className="h-4 w-4 mr-2" /> Resubmit Claim
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`denial-resolve-${denial.id}`} onSelect={() => handleMarkResolved(denial)}>
                          <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Resolved
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`denial-writeoff-${denial.id}`} onSelect={() => handleWriteOff(denial)}>
                          <X className="h-4 w-4 mr-2" /> Write Off
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* AI Fix Modal */}
      <Dialog open={modalType === 'ai-fix'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-md" data-testid="ai-fix-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" /> AI Claim Fix
            </DialogTitle>
            <DialogDescription>Review the AI recommendation for {selectedDenial?.claimId}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-slate-50 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Patient</span>
                <span className="font-medium">{selectedDenial?.patient}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Denial Reason</span>
                <span className="font-mono">{selectedDenial?.reason}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium text-red-600">{formatCurrency(selectedDenial?.amount || 0)}</span>
              </div>
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
              <p className="text-sm font-medium text-violet-800 mb-1">AI Recommendation</p>
              <p className="text-sm text-violet-700">{selectedDenial?.aiDetail}</p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleRejectAiFix} data-testid="ai-fix-reject-btn" className="gap-2">
              <X className="h-4 w-4" /> Reject
            </Button>
            <Button onClick={handleAcceptAiFix} data-testid="ai-fix-accept-btn" className="bg-emerald-600 hover:bg-emerald-700 gap-2">
              <CheckCircle2 className="h-4 w-4" /> Accept Fix
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={modalType === 'details'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-lg" data-testid="denial-details-modal">
          <DialogHeader>
            <DialogTitle>Denial Details: {selectedDenial?.id}</DialogTitle>
            <DialogDescription>Full details for claim {selectedDenial?.claimId}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ['Patient', selectedDenial?.patient],
              ['Payer', selectedDenial?.payer],
              ['Date of Service', selectedDenial?.dos],
              ['Denial Reason', `${selectedDenial?.reason} - ${selectedDenial?.reasonDesc}`],
              ['Amount', formatCurrency(selectedDenial?.amount || 0)],
              ['Days Open', `${selectedDenial?.daysOpen} days`],
              ['Status', selectedDenial?.status?.replace('_', ' ')],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium capitalize">{value}</span>
              </div>
            ))}
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 mt-2">
              <p className="text-xs font-medium text-violet-800 mb-1 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI Analysis</p>
              <p className="text-sm text-violet-700">{selectedDenial?.aiDetail}</p>
            </div>
            {/* Audit Trail */}
            {selectedDenial?.audit?.length > 0 && (
              <div className="rounded-lg border p-3 mt-2">
                <p className="text-xs font-semibold mb-2">Audit Trail</p>
                <div className="space-y-2">
                  {selectedDenial.audit.map((entry, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <div className="h-4 w-4 rounded-full bg-slate-200 flex items-center justify-center mt-0.5 shrink-0"><CheckCircle2 className="h-2.5 w-2.5 text-slate-500" /></div>
                      <div><p className="font-medium">{entry.action}</p><p className="text-muted-foreground">{entry.date} · {entry.user} · {entry.detail}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appeal Modal */}
      <Dialog open={modalType === 'appeal'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-md" data-testid="appeal-modal">
          <DialogHeader>
            <DialogTitle>File Appeal: {selectedDenial?.claimId}</DialogTitle>
            <DialogDescription>Submit an appeal for denial {selectedDenial?.reason}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Patient</span><span className="font-medium">{selectedDenial?.patient}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Payer</span><span className="font-medium">{selectedDenial?.payer}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-medium text-red-600">{formatCurrency(selectedDenial?.amount || 0)}</span></div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="appeal-reason">Appeal Reason</Label>
              <Textarea
                id="appeal-reason"
                data-testid="appeal-reason-input"
                placeholder="Describe why this denial should be overturned..."
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleFileAppeal} data-testid="submit-appeal-btn" className="bg-black hover:bg-gray-800">Submit Appeal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevQDenials;
